import {
	type BillCalculation,
	type BillTotals,
	calculateBill,
	type DiscountInput,
	type DiscountRule,
} from "@luckymenu/billing-core";
import { parseRupees, type StaffRole } from "@luckymenu/core";

/**
 * Discounts on the bill screen (BL-07): percent or amount, on the bill or on
 * one item, always with a reason. The screen recalculates the bill with
 * billing-core as staff type (`recalculate`); the server computes it again
 * when the bill is issued and refuses one that comes out different.
 */

/** A discount as the bill screen keeps it, and as the API takes it. */
export type BillDiscount = { rule: DiscountRule; reason: string; label: string | null };

/** The bill discount and the item discounts by order item ID. */
export type BillDiscounts = {
	bill: BillDiscount | null;
	items: Readonly<Record<string, BillDiscount>>;
};

export const NO_DISCOUNTS: BillDiscounts = { bill: null, items: {} };

/** What staff type in the discount form. */
export type DiscountDraft = { type: "percent" | "amount"; value: string; reason: string };

export const EMPTY_DISCOUNT_DRAFT: DiscountDraft = { type: "percent", value: "", reason: "" };

export type DiscountProblem = "required" | "invalid" | "tooMuch";

export type DiscountDraftResult =
	| { ok: true; discount: BillDiscount }
	| { ok: false; problems: { value?: DiscountProblem; reason?: DiscountProblem } };

/**
 * The typed discount, or what is wrong with it. A percent is up to two
 * decimals ("12.5") and at most 100; an amount is rupees ("50", "49.50").
 * An amount larger than the value it comes off is allowed: billing-core
 * stops it at the value.
 */
export function discountFromDraft(draft: DiscountDraft): DiscountDraftResult {
	const problems: { value?: DiscountProblem; reason?: DiscountProblem } = {};
	const reason = draft.reason.trim();
	if (reason === "") problems.reason = "required";
	else if (reason.length > 200) problems.reason = "invalid";
	let rule: DiscountRule | null = null;
	if (draft.value.trim() === "") problems.value = "required";
	else {
		// Hundredths: "12.5" % is 1250 basis points, "49.50" rupees is 4950 paise.
		const hundredths = parseRupees(draft.value);
		if (hundredths === null || hundredths === 0) problems.value = "invalid";
		else if (draft.type === "percent") {
			if (hundredths > 10000) problems.value = "tooMuch";
			else rule = { type: "percent", bp: hundredths, maxPaise: null };
		} else rule = { type: "amount", paise: hundredths };
	}
	if (!rule || problems.reason) return { ok: false, problems };
	return { ok: true, discount: { rule, reason, label: null } };
}

/** The form's fields for a discount given before, to change it. */
export function discountToDraft(discount: BillDiscount): DiscountDraft {
	const { rule } = discount;
	const hundredths = rule.type === "percent" ? rule.bp : rule.paise;
	const whole = Math.trunc(hundredths / 100);
	const rest = hundredths % 100;
	const value =
		rest === 0 ? String(whole) : `${whole}.${String(rest).padStart(2, "0").replace(/0$/, "")}`;
	return { type: rule.type, value, reason: discount.reason };
}

const toInput = (d: BillDiscount): DiscountInput => ({
	rule: d.rule,
	label: d.label,
	reason: d.reason,
	offerId: null,
});

/**
 * The bill with these discounts, from a calculation of the same lines
 * without them (the server's preview): the same settings, lines and rates,
 * through billing-core's `calculateBill`. Item discounts for lines no
 * longer on the bill are ignored.
 */
export function recalculate(base: BillCalculation, discounts: BillDiscounts): BillCalculation {
	return calculateBill({
		gstMode: base.gstMode,
		pricesIncludeTax: base.pricesIncludeTax,
		roundOff: base.roundOff,
		serviceChargeBp: base.serviceCharge?.rateBp ?? 0,
		// Only the service charge uses it, and the preview says which rate it took.
		defaultTaxRateBp: base.serviceCharge?.taxRateBp ?? 0,
		lines: base.lines.map((line) => {
			const staff = discounts.items[line.id];
			return { ...line, discount: staff ? toInput(staff) : line.discount };
		}),
		// A staff bill discount replaces the bill offer the server chose.
		billDiscount: discounts.bill ? toInput(discounts.bill) : offerOnly(base.billDiscount),
	});
}

/** The bill offer of a calculation as `calculateBill` takes it, or null for a staff discount. */
function offerOnly(discount: BillCalculation["billDiscount"]): DiscountInput | null {
	if (!discount?.offerId) return null;
	const { amountPaise: _, ...input } = discount;
	return input;
}

/**
 * What staff took off (not offers): the cashier's limit and the discount
 * permission count only this (P9-T04).
 */
export function staffDiscountOf(calculation: BillCalculation): number {
	const items = calculation.lines
		.filter((l) => l.discount && l.discount.offerId === null)
		.reduce((sum, l) => sum + l.itemDiscountPaise, 0);
	const bill =
		calculation.billDiscount && calculation.billDiscount.offerId === null
			? calculation.billDiscount.amountPaise
			: 0;
	return items + bill;
}

/** The discounts as `preview` and `finalize` take them. */
export function discountsInput(discounts: BillDiscounts, lineIds?: readonly string[]) {
	const keep = lineIds ? new Set(lineIds) : null;
	return {
		billDiscount: discounts.bill,
		itemDiscounts: Object.entries(discounts.items)
			.filter(([orderItemId]) => !keep || keep.has(orderItemId))
			.map(([orderItemId, discount]) => ({ orderItemId, discount })),
	};
}

export function hasDiscounts(discounts: BillDiscounts): boolean {
	return discounts.bill !== null || Object.keys(discounts.items).length > 0;
}

/**
 * The server's rule (P4-T11), to warn before the bill is issued: a cashier
 * may take off at most `limitBp` of the subtotal, item and bill discounts
 * together. Owners and managers have no limit.
 */
export function exceedsCashierLimit(
	role: StaffRole,
	limitBp: number,
	totals: Pick<BillTotals, "subtotalPaise" | "discountPaise">,
): boolean {
	if (role !== "cashier" || totals.discountPaise <= 0) return false;
	return totals.discountPaise * 10000 > limitBp * totals.subtotalPaise;
}
